import {GET_LIST, GET_ONE, GET_MANY, GET_MANY_REFERENCE, CREATE, UPDATE, DELETE} from 'react-admin';
import snakecase from 'lodash.snakecase';

import {RESOURCES} from '~constants/config';
import {
    formatCreateResource,
    parseResponse,
    parseResourceDetail,
    formatResponseData,
    formatShowProject,
    formatShowIirsaProject,
    formatProjectFormData,
    formatIirsaProjectFormData,
    parseProjectTracking,
    formatProjectTracking
} from '~utils/mappers';
import axios from 'axios';
import {getApiHeaders} from 'utils/helpers';
import {transformQueryParams, filterUnfinishedDateParams} from '~utils/object';

import api, {apiSetup} from '../api';
import {DELETE_MANY} from 'ra-core';
import HttpError from 'ra-core/lib/util/HttpError';
import {DocumentsService} from '~config/services/documents.service';
import {ImagesService} from '~config/services/images.service';
import {Document, Image} from '~components/GalleryItemsInput/classes';
import {formatRouteFormData, formatLayerFormData, formatLayerUpdateFormData} from "../../utils/mappers";

apiSetup();

function extractErrorMessage(data) {
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data.error === 'string') return data.error;
    if (typeof data.message === 'string') return data.message;
    const messages = [];
    for (const key of Object.keys(data)) {
        const val = data[key];
        if (Array.isArray(val)) {
            messages.push(`${key}: ${val.join(', ')}`);
        } else if (typeof val === 'string') {
            messages.push(`${key}: ${val}`);
        }
    }
    return messages.length > 0 ? messages.join('\n') : null;
}

function throwHttpError(response) {
    const status = response.status || 500;
    const data = response.data;
    const message = extractErrorMessage(data) || response.problem || response.statusText || 'Error desconocido';
    throw new HttpError(message, status, data);
}

export const PUBLISH = 'publish';
export const UNPUBLISH = 'unpublish';
export const PROMOTE = 'promote';

// A project's Seguimiento: a sub-resource of the project, addressed by project code.
// Etapa names come translated by request language; the backoffice is Spanish-only.
const PROJECT_TRACKING = 'project-tracking';
const projectTrackingUrl = code => `/projects/${encodeURIComponent(code)}/tracking/?lang=es`;

export default (type, resource, params) => {
    switch (type) {
        case GET_LIST: {
            const shared_params = {
                ...filterUnfinishedDateParams(transformQueryParams(params.filter)),
                page: params.pagination.page,
                page_size: params.pagination.perPage,
            };
            const getParams = {
                ...shared_params,
                sort: params.sort.field,
                order: params.sort.order
            };
            return api
                .get(`/${RESOURCES[resource]}`, getParams)
                .then(response => {
                    if (response.ok) {
                        console.log({response});
                        if (Array.isArray(response.data)) {
                            const formatted = formatResponseData[resource](response.data);
                            return { data: formatted, total: formatted.length };
                        }
                        response.data.page = formatResponseData[resource](response.data.page);
                        const parsedResponse = parseResponse(response);
                        return parsedResponse;
                    }
                    throwHttpError(response);
                });
        }
        case GET_ONE: {
            if (resource === PROJECT_TRACKING) {
                return api.get(projectTrackingUrl(params.id)).then(response => {
                    if (response.ok) {
                        return {data: parseProjectTracking(response.data, params.id)};
                    }
                    throwHttpError(response);
                });
            }
            return api.get(`/${RESOURCES[resource]}/${params.id}`).then(response => {
                if (response.ok) {
                    let parser = parseResourceDetail[resource];
                    if (resource == 'projects' && window.location.href.includes('/show')) {
                        parser = formatShowIirsaProject;
                    }
                    if (!parser) {
                        parser = (data) => data;
                    }
                    return {data: parser(response.data)};
                }
                throwHttpError(response);
            });
        }
        case GET_MANY: {
            const ids = params.ids;
            return Promise.all(
                ids.map(id => api.get(`/${RESOURCES[resource]}/${id}`).then(response => {
                    if (response.ok) {
                        return response.data;
                    }
                    throwHttpError(response);
                }))
            ).then(data => ({data}));
        }
        case GET_MANY_REFERENCE: {
            const getParams = {
                ...filterUnfinishedDateParams(transformQueryParams(params.filter || {})),
                page: params.pagination.page,
                page_size: params.pagination.perPage,
                sort: params.sort.field,
                order: params.sort.order,
                [params.target]: params.id
            };
            return api
                .get(`/${RESOURCES[resource]}`, getParams)
                .then(response => {
                    if (response.ok) {
                        if (Array.isArray(response.data)) {
                            return {data: response.data, total: response.data.length};
                        }
                        const parsedResponse = parseResponse(response);
                        return parsedResponse;
                    }
                    throwHttpError(response);
                });
        }
        case CREATE: {
            const api_url = `${RESOURCES[resource]}/`;

            if (resource == 'projects') {
                const formData = createIirsaProjectFormData(params.data);
                let baseUrl = process.env.REACT_APP_BASE_URL;
                let headers = getApiHeaders();
                return axios.post(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        const data = response.data;
                        setTimeout(function () {
                            location.reload()
                        }, 2000);
                        return {data: {...data, id: data.code}};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            } else if (resource == 'draft-projects') {
                const formData = createIirsaProjectFormData(params.data);
                let baseUrl = process.env.REACT_APP_BASE_URL;
                let headers = getApiHeaders();
                return axios.post(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        const data = response.data;
                        return {data: {...data, id: data.uuid}};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            } else if (resource == 'routes') {
                const formData = createRouteFormData(params.data);
                let baseUrl = process.env.REACT_APP_BASE_URL;
                let headers = getApiHeaders();
                return axios.post(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        setTimeout(function () {
                            location.reload()
                        }, 2000);
                        return {data: response.data};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            } else if (resource == 'paises') {
                const formData = buildPaisFormData(params.data);
                const baseUrl = process.env.REACT_APP_BASE_URL;
                const headers = getApiHeaders();
                return axios.post(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        return {data: response.data};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            } else if (resource == 'layers') {
                const {name, files} = formatLayerFormData(params.data);
                const baseUrl = process.env.REACT_APP_BASE_URL;
                const headers = getApiHeaders();
                const url = `${baseUrl}${api_url}`;

                const buildFormData = replace => {
                    const formData = new FormData();
                    formData.append('name', name);
                    // Repeated 'files' parts so DRF request.FILES.getlist('files') reads them.
                    files.forEach(file => formData.append('files', file));
                    if (replace) {
                        formData.append('replace', 'true');
                    }
                    return formData;
                };

                const postLayer = replace =>
                    axios.post(url, buildFormData(replace), {headers, timeout: 0})
                        .then(response => {
                            if (response.status >= 200 && response.status < 300) {
                                return {data: response.data};
                            }
                            throwHttpError(response);
                        });

                // First attempt without replace. If the name already exists the backend
                // returns 409; ask the user whether to overwrite, and only then re-POST
                // with replace=true. Any other error propagates as usual.
                return postLayer(false).catch(error => {
                    const resp = error.response;
                    if (resp && resp.status === 409 && resp.data && resp.data.code === 'name_exists') {
                        const confirmed = window.confirm(
                            `Ya existe una capa llamada "${name}".\n\n` +
                            'Aceptar: reemplazar los datos de la capa existente.\n' +
                            'Cancelar: abortar.'
                        );
                        if (!confirmed) {
                            throw new HttpError('Carga cancelada.', 409, resp.data);
                        }
                        return postLayer(true).catch(replaceError => {
                            throwHttpError(replaceError.response
                                || {status: 500, data: {error: replaceError.message}});
                        });
                    }
                    throwHttpError(resp || {status: 500, data: {error: error.message}});
                });
            } else if (resource == 'topics' || resource == 'priorizaciones' || resource == 'disclaimers'
                || resource == 'alcances' || resource == 'estados-estudios' || resource == 'estados'
                || resource == 'financiadores-estudios' || resource == 'tipos-proyecto'
                || resource == 'sectores' || resource == 'subsectores'
                || resource == 'metrics' || resource == 'metric-values'
                || resource == 'users') {
                const creationParams = formatCreateResource[resource](params.data);
                return api.post(`/${api_url}`, creationParams).then(response => {
                    if (response.ok) {
                        const parser = parseResourceDetail[resource] || (data => data);
                        return {data: parser(response.data)};
                    }
                    throwHttpError(response);
                });
            } else {
                const creationParams = formatCreateResource[resource](params.data);
                return api.post(`/${api_url}`, creationParams).then(response => {
                    const docService = new DocumentsService();
                    return docService.create(resource, params.data, response.data.id).then((r) => {
                        if (response.ok) {
                            return {data: response.data};
                        }
                        throwHttpError(response);
                    });
                });
            }
        }
        case PUBLISH: {
            return api.patch(`/${RESOURCES[resource]}/${params.id}/publish/`);
        }
        case UNPUBLISH: {
            return api.patch(`/${RESOURCES[resource]}/${params.id}/unpublish/`);
        }
        case PROMOTE: {
            return api.post(`/${RESOURCES[resource]}/${params.id}/promote/`).then(response => {
                if (response.ok) {
                    return {data: response.data};
                }
                throwHttpError(response);
            });
        }
        case DELETE: {
            if (resource === PROJECT_TRACKING) {
                return api.delete(projectTrackingUrl(params.id)).then(response => {
                    if (response.ok) {
                        return {data: {id: params.id}};
                    }
                    throwHttpError(response);
                });
            }
            return doDelete(resource, params.id);
        }
        case UPDATE: {
            if (resource === PROJECT_TRACKING) {
                return api.put(projectTrackingUrl(params.id), formatProjectTracking(params.data)).then(response => {
                    if (response.ok) {
                        return {data: parseProjectTracking(response.data, params.id)};
                    }
                    throwHttpError(response);
                });
            }
            const api_url = `${RESOURCES[resource]}/${params.id}/`;
            if (resource == 'paises') {
                const formData = buildPaisFormData(params.data);
                const baseUrl = process.env.REACT_APP_BASE_URL;
                const headers = getApiHeaders();
                return axios.put(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        return {data: response.data};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            }
            if (resource == 'layers') {
                const fields = formatLayerUpdateFormData(params.data);
                const baseUrl = process.env.REACT_APP_BASE_URL;
                const headers = getApiHeaders();
                const formData = new FormData();
                // Only fields the operator actually touched are appended -- an
                // empty payload is a valid no-op PATCH (see formatLayerUpdateFormData).
                if (fields.publishedName !== undefined) {
                    formData.append('publishing_published_name', fields.publishedName || '');
                }
                if (fields.title !== undefined) {
                    formData.append('publishing_title', fields.title || '');
                }
                if (fields.titleEn !== undefined) {
                    formData.append('publishing_title_en', fields.titleEn || '');
                }
                if (fields.titlePt !== undefined) {
                    formData.append('publishing_title_pt', fields.titlePt || '');
                }
                if (fields.workspace !== undefined) {
                    formData.append('publishing_workspace', fields.workspace || '');
                }
                if (fields.store !== undefined) {
                    formData.append('publishing_store', fields.store || '');
                }
                if (fields.srs !== undefined) {
                    formData.append('publishing_srs', fields.srs || '');
                }
                if (fields.sld !== undefined) {
                    formData.append('publishing_sld', fields.sld || '');
                }
                if (fields.sldFile) {
                    formData.append('publishing_sld_file', fields.sldFile);
                }
                if (fields.enabled !== undefined) {
                    formData.append('publishing_enabled', fields.enabled ? 'true' : 'false');
                }
                // Optional replacement shapefile: the API re-ingests, recomputes metrics
                // and republishes. Repeated 'files' parts, as in CREATE.
                formatLayerFormData(params.data).files.forEach(file => formData.append('files', file));
                return axios.put(`${baseUrl}${api_url}`, formData, {headers, timeout: 0}).then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        return {data: response.data};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            }
            if (resource == 'priorizaciones' || resource == 'disclaimers' || resource == 'etapas'
                || resource == 'alcances' || resource == 'estados-estudios' || resource == 'estados'
                || resource == 'financiadores-estudios' || resource == 'tipos-proyecto'
                || resource == 'sectores' || resource == 'subsectores'
                || resource == 'metrics' || resource == 'metric-values'
                || resource == 'users') {
                const updateParams = formatCreateResource[resource](params.data);
                return api.put(`/${api_url}`, updateParams).then(response => {
                    if (response.ok) {
                        const parser = parseResourceDetail[resource] || (data => data);
                        return {data: parser(response.data)};
                    }
                    throwHttpError(response);
                });
            }
            if (resource == 'draft-projects') {
                const draftFormData = createIirsaProjectFormData(params.data);
                const draftBaseUrl = process.env.REACT_APP_BASE_URL;
                const draftHeaders = getApiHeaders();
                return axios.put(`${draftBaseUrl}${api_url}`, draftFormData, {headers: draftHeaders}).then(response => {
                    const {data, status} = response;
                    if (status == 200) {
                        return {data: {...data, id: data.uuid}};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            }
            let formData;
            if (resource == 'projects') {
                formData = createIirsaProjectFormData(params.data);

            } else {
                formData = createRouteFormData(params.data);
            }
            let baseUrl = process.env.REACT_APP_BASE_URL;
            let headers = getApiHeaders();
            return axios.put(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                const {data, status} = response
                if (status == 200) {
                    return {data: {...data, id: data.code}};
                }
                throwHttpError(response);
            }).catch(error => {
                throwHttpError(error.response || {status: 500, data: {error: error.message}});
            });
        }
        case DELETE_MANY: {
            deleteMany(resource, params.ids[0], params.ids);
            return {data: [...params.ids]};
        }
        default:
            throw new Error('provider.invalidAction');
    }
};

function buildPaisFormData(data) {
    const formData = new FormData();
    if (data.name !== undefined && data.name !== null) {
        formData.append('name', data.name);
    }
    if (data.flag && data.flag.rawFile) {
        formData.append('flag', data.flag.rawFile);
    }
    return formData;
}

function createFormData(params) {
    let formData = new FormData();
    let data = formatProjectFormData(params);
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    return formData;
}

function mapArrayToFormData(formData, key, array) {
    array.forEach((item, idx) => {
        mapValueToFormData(formData, `${key}[${idx}]`, item);
    });
    return formData;
}

function mapValueToFormData(formData, key, value) {
    if (value instanceof Object) {
        if (value instanceof Date) {
            formData.append(key, value.toISOString());
        } else if (value instanceof File) {
            formData.append(key, value);
        } else if (value instanceof Array) {
            mapArrayToFormData(formData, key, value);
        } else {
            mapObjectToFormData(formData, key, value);
        }
    } else {
        if (value !== undefined && value !== null) {
            formData.append(key, value);
        }
    }
    return formData;
}

function mapObjectToFormData(formData, key, obj) {
    Object.keys(obj).forEach(subKey => {
        mapValueToFormData(formData, `${key}${!key || key.endsWith(']') ? '' : '.'}${subKey}`, obj[subKey]);
    });
    return formData;
}

function createIirsaProjectFormData(params) {
    let formData = new FormData();
    let data = formatIirsaProjectFormData(params);
    mapValueToFormData(formData, '', data);
    return formData;
}

function createRouteFormData(params) {
    let formData = new FormData();
    let data = formatRouteFormData(params);
    mapValueToFormData(formData, '', data);
    return formData;

}

function createItems(items, id, resource) {
    let documents = items.filter(gi => gi instanceof Document);
    let images = items.filter(gi => gi instanceof Image);
    return uploadDocuments(id, documents, resource).then(_ => {
        return uploadImages(id, images, resource);
    });
}

async function uploadDocuments(id, documents, resource) {
    const docService = new DocumentsService();
    const currentDocs = new Array();
    for (let idx in documents) {
        let doc = documents[idx];
        if (!doc.id) {
            let response = await docService.create(resource, doc, id);
            currentDocs.push(response.data.id);
        } else {
            let response = await docService.update(doc, resource);
            currentDocs.push(response.data.id);
        }
    }
    return docService.deleteMany(currentDocs, id, resource === RESOURCES.projects ? 'Project' : 'iirsaProject');
}

async function uploadImages(id, images, resource) {
    const imgService = new ImagesService(resource);
    const currentImgs = new Array();
    for (let idx in images) {
        let img = images[idx]
        if (!img.id) {
            let response = await imgService.create(id, img);
            currentImgs.push(response.data.id)
        } else {
            let response = await imgService.update(img);
            currentImgs.push(response.data.id)
        }
    }
    return imgService.deleteMany(currentImgs, id);
}

function deleteMany(resource, id, idList) {
    let res = doDelete(resource, id);
    res.then(r => {
        let idx = idList.indexOf(id);
        if (idx < (idList.length - 1)) {
            deleteMany(resource, idList[idx + 1], idList);
        }
    });
}

function doDelete(resource, id) {
    return api.delete(`/${RESOURCES[resource]}/${id}/`).then(response => {
        if (response.ok) {
            return {data: {id: id}};
        }
        throwHttpError(response);
    });
}
